import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatQuantity,
  resolveBaseAmount,
  scaleNutrients,
  sumNutrients,
  type CanonicalUnit,
  type Nutrients,
  type Recipe,
  type RecipeComponentInput,
} from '@nutrigeist/shared';
import { recipeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import {
  BarcodePanel,
  LabelPanel,
  SearchPanel,
  type PickedFood,
} from '../components/foodSources.js';
import { LogFoodSheet, type LoggableFood } from '../components/LogFoodSheet.js';
import {
  BarcodeIcon,
  CameraIcon,
  ChevronLeftIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '../components/icons.js';
import { formatGrams, formatKcal } from '../lib/format.js';

/**
 * A component while it is being edited, before the server resolves it.
 *
 * The nutrient values are carried alongside the reference purely so the editor
 * can show a running total without a round trip. The server recomputes
 * everything on save — this is a preview, never the source of truth.
 */
interface DraftComponent {
  food: LoggableFood;
  quantity: { amount: number; unit: CanonicalUnit };
  grams: number;
  totals: Nutrients;
}

type Source = 'search' | 'barcode' | 'label';

const SOURCES: {
  id: Source;
  label: string;
  Icon: (props: { className?: string }) => JSX.Element;
}[] = [
  { id: 'search', label: 'Suche', Icon: SearchIcon },
  { id: 'barcode', label: 'Barcode', Icon: BarcodeIcon },
  { id: 'label', label: 'Label', Icon: CameraIcon },
];

export function RecipesPage(): JSX.Element {
  const [editing, setEditing] = useState<Recipe | 'new' | null>(null);

  const recipes = useQuery({ queryKey: ['recipes'], queryFn: () => recipeApi.list() });

  if (editing) {
    return (
      <RecipeEditor recipe={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
    );
  }

  return (
    <div className="stack">
      <div className="page-header">
        <h1>Rezepte</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          <PlusIcon />
          Neu
        </button>
      </div>

      {recipes.isPending && (
        <div className="empty-state">
          <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
        </div>
      )}

      {recipes.data?.recipes.length === 0 && (
        <div className="card empty-state">
          Noch keine Rezepte. Lege eines an, um mehrere Zutaten als eine Einheit zu erfassen.
        </div>
      )}

      {recipes.data && recipes.data.recipes.length > 0 && (
        <div className="card">
          <div className="entry-list">
            {recipes.data.recipes.map((recipe) => (
              <button
                key={recipe.id}
                className="entry entry-button"
                onClick={() => setEditing(recipe)}
              >
                <div className="grow">
                  <div className="entry-name">{recipe.name}</div>
                  <div className="entry-meta">
                    {recipe.components.length}{' '}
                    {recipe.components.length === 1 ? 'Zutat' : 'Zutaten'} ·{' '}
                    {formatGrams(recipe.totalGrams)} g · {formatKcal(recipe.nutrients.kcal)} kcal /
                    100 g
                  </div>
                </div>
                <span className="entry-kcal">{formatKcal(recipe.totals.kcal)} kcal</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RecipeEditor({
  recipe,
  onDone,
}: {
  recipe: Recipe | null;
  onDone: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();

  const [name, setName] = useState(recipe?.name ?? '');
  const [servings, setServings] = useState(recipe?.servings?.toString() ?? '');
  const [components, setComponents] = useState<DraftComponent[]>(() =>
    (recipe?.components ?? []).map((component) => ({
      food: {
        id: component.foodId,
        name: component.name,
        brand: component.brand,
        basis: component.basis,
        nutrients: component.per100,
        serving: null,
        density: null,
      },
      quantity: component.quantity,
      grams: component.grams,
      totals: component.totals,
    })),
  );

  const [source, setSource] = useState<Source | null>(null);
  const [picked, setPicked] = useState<PickedFood | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalGrams = components.reduce((sum, component) => sum + component.grams, 0);
  const totals = sumNutrients(components.map((component) => component.totals));
  const per100 = totalGrams > 0 ? scaleNutrients(totals, 100 / totalGrams) : null;

  function addComponent(food: LoggableFood, quantity: { amount: number; unit: CanonicalUnit }) {
    const baseAmount = resolveBaseAmount(food, quantity);

    setComponents((current) => [
      ...current,
      {
        food,
        quantity,
        // Mirrors the server: a millilitre-based ingredient contributes mass,
        // not volume, so the running total stays a weight.
        grams: food.basis === 'per_100ml' ? baseAmount * (food.density ?? 1) : baseAmount,
        totals: scaleNutrients(food.nutrients, baseAmount / 100),
      },
    ]);

    setPicked(null);
    setSource(null);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        servings: servings.trim() === '' ? null : Number(servings.replace(',', '.')),
        components: components.map<RecipeComponentInput>((component) => ({
          // A catalog food goes by reference; anything else (a label scan)
          // travels inline with its own values.
          food: component.food.id
            ? { foodId: component.food.id }
            : {
                name: component.food.name,
                brand: component.food.brand,
                basis: component.food.basis,
                nutrients: component.food.nutrients,
                serving: component.food.serving,
                density: component.food.density,
                saveToCatalog: false,
              },
          quantity: component.quantity,
        })),
      };

      return recipe ? recipeApi.update(recipe.id, payload) : recipeApi.create(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recipes'] });
      onDone();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: () => recipeApi.remove(recipe!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recipes'] });
      onDone();
    },
  });

  return (
    <div className="stack">
      <div className="page-header">
        <button className="icon-btn" onClick={onDone} aria-label="Zurück">
          <ChevronLeftIcon />
        </button>
        <h1 className="grow">{recipe ? 'Rezept bearbeiten' : 'Neues Rezept'}</h1>
      </div>

      <div className="card stack">
        <div className="field">
          <label htmlFor="recipe-name">Name</label>
          <input
            id="recipe-name"
            className="input"
            placeholder="z. B. Chili con Carne"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="recipe-servings">Portionen (optional)</label>
          <input
            id="recipe-servings"
            className="input"
            inputMode="decimal"
            placeholder="z. B. 4"
            value={servings}
            onChange={(event) => setServings(event.target.value)}
          />
          <span className="hint">
            Mit Portionsangabe kannst du später „1 Portion“ statt Gramm erfassen.
          </span>
        </div>
      </div>

      <div className="card stack">
        <div className="card-title">Zutaten</div>

        {components.length === 0 ? (
          <div className="empty-state">Noch keine Zutaten.</div>
        ) : (
          <div className="entry-list">
            {components.map((component, index) => (
              <div className="entry" key={`${component.food.name}-${index}`}>
                <div className="grow">
                  <div className="entry-name">{component.food.name}</div>
                  <div className="entry-meta">
                    {formatQuantity(component.quantity)}
                    {component.food.basis === 'per_100ml'
                      ? ` · ${formatGrams(component.grams)} g`
                      : ''}
                  </div>
                </div>
                <span className="entry-kcal">{formatKcal(component.totals.kcal)} kcal</span>
                <button
                  className="icon-btn"
                  aria-label={`${component.food.name} entfernen`}
                  onClick={() => setComponents((current) => current.filter((_, i) => i !== index))}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        {per100 && (
          <div className="card card-tight">
            <div className="row-between">
              <span className="hint">Gesamt</span>
              <strong>
                {formatGrams(totalGrams)} g · {formatKcal(totals.kcal)} kcal
              </strong>
            </div>
            <div className="row-between">
              <span className="hint">Pro 100 g</span>
              <strong>
                {formatKcal(per100.kcal)} kcal · E {formatGrams(per100.protein)} · KH{' '}
                {formatGrams(per100.carbs)} · F {formatGrams(per100.fat)}
              </strong>
            </div>
          </div>
        )}

        {source ? (
          <div className="stack">
            <div className="segmented">
              {SOURCES.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  className={source === id ? 'active' : ''}
                  onClick={() => setSource(id)}
                >
                  <Icon />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {source === 'search' && <SearchPanel onPicked={setPicked} />}
            {source === 'barcode' && (
              <BarcodePanel onPicked={setPicked} onScanLabel={() => setSource('label')} />
            )}
            {source === 'label' && (
              <LabelPanel onPicked={setPicked} onCancel={() => setSource(null)} />
            )}

            <button className="btn btn-ghost" onClick={() => setSource(null)}>
              Abbrechen
            </button>
          </div>
        ) : (
          <button className="btn btn-ghost btn-block" onClick={() => setSource('search')}>
            <PlusIcon />
            Zutat hinzufügen
          </button>
        )}
      </div>

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      <button
        className="btn btn-primary btn-block"
        disabled={save.isPending || name.trim().length === 0}
        onClick={() => save.mutate()}
      >
        {save.isPending ? <span className="spinner" /> : 'Rezept speichern'}
      </button>

      {recipe && (
        <button
          className="btn btn-danger btn-block"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
        >
          Rezept löschen
        </button>
      )}

      {picked && (
        <LogFoodSheet
          food={picked.food}
          warnings={picked.warnings}
          submitLabel="Zutat übernehmen"
          onSubmit={addComponent}
          onClose={() => setPicked(null)}
          onLogged={() => setPicked(null)}
        />
      )}
    </div>
  );
}
